// Wraps an item / upgrade card container as a MenuNav item: focus = selected
// frame + slight lift, click / confirm = activate. Used by the Reward and
// Station overlays.

import Phaser from 'phaser';
import { getCardHandle } from './ItemCard';
import type { MenuNav, NavItem, NavRect } from './nav';

export interface CardNavOptions {
  onActivate: () => void;
  enabled?: () => boolean;
  /** Scale while focused (default 1.035). */
  focusScale?: number;
  /** Base scale of the card (when cards are shrunk to fit). */
  baseScale?: number;
}

export class CardNavItem implements NavItem {
  private focusedFlag = false;
  readonly baseScale: number;

  constructor(
    readonly card: Phaser.GameObjects.Container,
    private readonly opts: CardNavOptions,
  ) {
    this.baseScale = opts.baseScale ?? 1;
    card.setScale(this.baseScale);
  }

  get focused(): boolean {
    return this.focusedFlag;
  }

  navBounds(): NavRect {
    const h = getCardHandle(this.card);
    const m = this.card.getWorldTransformMatrix();
    return { x: m.tx, y: m.ty, w: (h?.width ?? this.card.width) * m.scaleX, h: (h?.height ?? this.card.height) * m.scaleY };
  }

  setFocused(on: boolean): void {
    if (on === this.focusedFlag) return;
    this.focusedFlag = on;
    getCardHandle(this.card)?.setSelected(on);
    const s = this.card.scene;
    if (!s) return;
    s.tweens.killTweensOf(this.card);
    const target = this.baseScale * (on ? (this.opts.focusScale ?? 1.035) : 1);
    s.tweens.add({ targets: this.card, scale: target, duration: on ? 160 : 140, ease: on ? 'Back.easeOut' : 'Cubic.easeOut' });
    if (on) this.card.parentContainer ? this.card.parentContainer.bringToTop(this.card) : s.children.bringToTop(this.card);
  }

  activate(): void {
    this.opts.onActivate();
  }

  isEnabled(): boolean {
    return this.opts.enabled ? this.opts.enabled() : true;
  }

  /** Register with a nav and make the card clickable. */
  bind(nav: MenuNav): this {
    this.card.setInteractive({ useHandCursor: false });
    nav.add(this);
    nav.bindPointer(this, this.card);
    return this;
  }
}
